import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Send, Eye, Mail, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function AdminDigest() {
  const [status, setStatus] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const load = async () => {
    try { const r = await api.get("/digest/status"); setStatus(r.data); } catch { toast.error("Failed to load digest status"); }
  };
  useEffect(() => { load(); }, []);

  const openPreview = async () => {
    setLoadingPreview(true);
    setPreviewOpen(true);
    try {
      const r = await api.get("/digest/preview");
      setPreviewHtml(r.data);
    } catch { toast.error("Failed to fetch preview"); }
    finally { setLoadingPreview(false); }
  };

  const dryRun = async () => {
    try {
      const r = await api.post("/digest/send?dry_run=true");
      toast.success(`Dry run: would send to ${r.data.would_send} subscribers`, { description: r.data.subject });
    } catch (e) { toast.error(e?.response?.data?.detail || "Dry run failed"); }
  };

  const sendNow = async () => {
    if (!window.confirm(`Send the digest to all subscribers right now?`)) return;
    setSending(true);
    try {
      const r = await api.post("/digest/send");
      if (r.data.skipped) {
        toast.message("Digest skipped", { description: r.data.skipped.replace(/_/g, " ") });
      } else {
        toast.success(`Sent to ${r.data.sent} subscribers (${r.data.entries_in_digest} entries)`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-10">
      <Link to="/admin" className="inline-flex items-center gap-2 font-mono-plex text-xs uppercase tracking-wider hover:underline" data-testid="back-to-admin">
        <ArrowLeft className="w-4 h-4" /> Back to admin
      </Link>
      <div className="mt-4 border-b border-black/15 pb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">/the dispatch</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-2">Weekly Digest</h1>
          <p className="text-sm text-[#555] mt-2 max-w-xl">
            Editorial-controlled digest sent via Resend. Auto-runs Mondays 14:00 UTC; you can also preview, dry-run, or send-now from here.
          </p>
        </div>
        <Button onClick={load} variant="outline" className="rounded-none border-black h-10" data-testid="digest-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {status && (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-black/15 border border-black/15">
          <Stat label="Resend connected" value={status.resend_configured ? "Yes" : "No — set RESEND_API_KEY"} accent={status.resend_configured ? "ok" : "warn"} />
          <Stat label="Sender" value={status.sender_email} mono />
          <Stat label="Subscribers" value={status.subscribers} />
          <Stat label="Auto weekly" value={status.auto_weekly ? "Mondays 14:00 UTC" : "Disabled"} />
          <Stat label="Last sent" value={status.last_digest_sent_at ? new Date(status.last_digest_sent_at).toLocaleString() : "Never"} className="sm:col-span-2" />
          <Stat label="Last run summary" value={
            status.last_digest_run
              ? `${status.last_digest_run.sent} sent · ${status.last_digest_run.entries} entries · by ${status.last_digest_run.by}`
              : "—"
          } className="sm:col-span-2" />
        </div>
      )}

      <div className="mt-8 flex flex-wrap gap-2">
        <Button onClick={openPreview} variant="outline" className="rounded-none border-black h-11" data-testid="digest-preview-btn">
          <Eye className="w-4 h-4 mr-2" /> Preview
        </Button>
        <Button onClick={dryRun} variant="outline" className="rounded-none border-[#0050FF] text-[#0050FF] h-11" data-testid="digest-dryrun-btn">
          <Mail className="w-4 h-4 mr-2" /> Dry run
        </Button>
        <Button onClick={sendNow} disabled={sending || !status?.resend_configured} className="rounded-none bg-black text-white h-11 disabled:opacity-40" data-testid="digest-send-btn">
          <Send className="w-4 h-4 mr-2" /> {sending ? "Sending…" : "Send digest now"}
        </Button>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="rounded-none max-w-3xl border-2 border-black p-0 gap-0 bg-white max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="px-6 py-4 border-b border-black/15">
            <DialogTitle className="font-display text-2xl font-bold tracking-tight">Digest preview</DialogTitle>
            <DialogDescription className="text-xs font-mono-plex uppercase tracking-wider text-[#555]">
              This is exactly what subscribers will receive.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto bg-[#F5F5F5] p-4" data-testid="digest-preview-content">
            {loadingPreview
              ? <div className="font-mono-plex text-sm text-[#555] uppercase tracking-wider p-8 text-center">Loading…</div>
              : <iframe title="Digest preview" srcDoc={previewHtml} className="w-full bg-white border border-black/20" style={{ height: "65vh" }} sandbox="" data-testid="digest-iframe" />
            }
          </div>
          <DialogFooter className="px-6 py-4 border-t border-black/15 bg-white">
            <Button variant="outline" onClick={() => setPreviewOpen(false)} className="rounded-none border-black/40 h-10">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, accent, mono, className = "" }) {
  const color = accent === "ok" ? "text-[#008A00]" : accent === "warn" ? "text-[#D32F2F]" : "text-black";
  return (
    <div className={`p-4 bg-white ${className}`}>
      <div className="font-mono-plex text-[11px] uppercase tracking-wider text-[#555]">{label}</div>
      <div className={`mt-1 ${mono ? "font-mono-plex text-sm" : "font-display text-xl font-bold"} ${color}`}>{value}</div>
    </div>
  );
}
